import React, { useState, useRef, useEffect } from 'react';
import { X, Send, MessageSquare } from 'lucide-react'; // Adicionei MessageSquare para o botão de rasto
import { Button } from '../components/ui/button';
import { useLanguage } from '../i18n';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// URL da foto da Louzinha
const LOUZINHA_PHOTO = 'https://customer-assets.emergentagent.com/job_aesthetic-hub-16/artifacts/hi7s1xvq_louzinha.jpg';

const Louzinha = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showBubble, setShowBubble] = useState(true);
  const [isFullyHidden, setIsFullyHidden] = useState(false); // Nova variável para ocultar totalmente
  const messagesEndRef = useRef(null);
  const { t, language } = useLanguage();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([
        {
          id: 'greeting',
          role: 'assistant',
          content: t('chat.greeting'),
        },
      ]);
    }
  }, [isOpen, t, messages.length]);

  // Esconder balão após alguns segundos ou quando abrir o chat
  useEffect(() => {
    if (isOpen) {
      setShowBubble(false);
    }
  }, [isOpen]);

  // Mostrar balão periodicamente
  useEffect(() => {
    if (!isOpen && !isFullyHidden) {
      const timer = setTimeout(() => setShowBubble(true), 3000);
      return () => clearTimeout(timer);
    }
  }, [isOpen, isFullyHidden]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    const userMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: input.trim(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/chat`, {
        message: userMessage.content,
        language,
        // --- ADIÇÃO PARA ACESSO À AGENDA ---
        allowCalendarAccess: true,
        tools: ['check_availability', 'create_booking', 'get_services']
      });

      const assistantMessage = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: response.data.response,
      };

      setMessages((prev) => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Chat error:', error);
      const errorMessage = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: language === 'pt' 
          ? 'Desculpe, ocorreu um erro. Por favor, tente novamente.'
          : 'Sorry, an error occurred. Please try again.',
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  // Se estiver totalmente oculto, mostra apenas um botão minúsculo de recuperação
  if (isFullyHidden) {
    return (
      <button 
        onClick={() => setIsFullyHidden(false)}
        className="fixed bottom-24 right-4 bg-[#BFA05F] text-white p-3 rounded-full shadow-lg z-[60] hover:scale-110 transition-all"
      >
        <MessageSquare size={20} />
      </button>
    );
  }

  return (
    <>
      {/* Speech Bubble */}
{showBubble && !isOpen && (
  <div 
    className="fixed bottom-40 sm:bottom-48 right-6 sm:right-8 z-[70] animate-fade-in-up cursor-pointer" 
    onClick={() => setIsOpen(true)}
  >
    {/* O Balão deslocado para a esquerda com mr-8 */}
    <div className="bg-white rounded-2xl rounded-br-none px-4 py-3 shadow-lg border border-[#BFA05F] max-w-[180px] sm:max-w-[220px] mr-6">
      <p className="text-xs sm:text-sm text-[#1A1815] font-medium">
        {language === 'pt' ? 'Olá! Sou a Louzinha 👋' : 'Hi! I\'m Louzinha 👋'}
      </p>
      <p className="text-[10px] sm:text-xs text-[#635E54] mt-1">
        {language === 'pt' ? 'Posso ajudar com agendamentos!' : 'I can help with bookings!'}
      </p>
    </div>
    
    {/* Triangle pointer ajustado para a ponta direita inferior */}
    <div className="absolute bottom-0 right-6 w-0 h-0 border-l-[10px] border-l-transparent border-t-[10px] border-t-white transform translate-y-full" />
  </div>
)}

      {/* Botão de Fechar Total (Aparece apenas quando o chat está fechado) */}
      {!isOpen && (
        <button 
          onClick={(e) => { e.stopPropagation(); setIsFullyHidden(true); }}
          className="fixed bottom-32 sm:bottom-44 right-2 sm:right-4 z-[61] bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors shadow-sm"
          title="Ocultar Louzinha"
        >
          <X size={12} />
        </button>
      )}

      {/* Floating Button with Photo */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed bottom-24 sm:bottom-28 right-4 sm:right-6 z-[60] w-12 h-12 sm:w-16 sm:h-16 rounded-full flex items-center justify-center shadow-2xl transition-all duration-300 overflow-hidden ${
          isOpen
            ? 'bg-[#2C2924]'
            : 'hover:scale-110 ring-2 ring-[#BFA05F] ring-offset-2'
        }`}
        data-testid="louzinha-toggle"
      >
        {isOpen ? (
          <X className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
        ) : (
          <img 
            src={LOUZINHA_PHOTO} 
            alt="Louzinha - Assistente IA"
            className="w-full h-full object-cover"
          />
        )}
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div
          className="fixed bottom-36 sm:bottom-48 right-2 sm:right-6 left-2 sm:left-auto z-[60] sm:w-96 h-[400px] sm:h-[450px] rounded-2xl shadow-2xl border border-[#E6E2DA] bg-white/95 backdrop-blur-md flex flex-col overflow-hidden animate-fade-in-up"
          data-testid="louzinha-chat"
        >
          {/* Header */}
          <div className="bg-[#1A1815] text-white p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-[#BFA05F]">
                <img 
                    src={LOUZINHA_PHOTO} 
                    alt="Louzinha"
                    className="w-full h-full object-cover"
                />
                </div>
                <div>
                <h3 className="font-heading font-semibold">{t('chat.title')}</h3>
                <p className="text-xs text-[#A8A29A]">{t('chat.subtitle')}</p>
                </div>
            </div>
            {/* Botão para fechar o chat e voltar à bolinha */}
            <button onClick={() => setIsOpen(false)} className="hover:text-[#BFA05F]">
                <X size={20} />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-[#F9F8F6]">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${
                  message.role === 'user' ? 'justify-end' : 'justify-start'
                }`}
              >
                {message.role === 'assistant' && (
                  <div className="w-8 h-8 rounded-full overflow-hidden mr-2 flex-shrink-0">
                    <img src={LOUZINHA_PHOTO} alt="" className="w-full h-full object-cover" />
                  </div>
                )}
                <div
                  className={`max-w-[75%] rounded-2xl px-4 py-3 ${
                    message.role === 'user'
                      ? 'bg-[#1A1815] text-white rounded-br-md'
                      : 'bg-white text-[#2C2924] rounded-bl-md shadow-sm border border-[#E6E2DA]'
                  }`}
                >
                  <p className="text-sm leading-relaxed whitespace-pre-wrap">
                    {message.content}
                  </p>
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="flex justify-start">
                <div className="w-8 h-8 rounded-full overflow-hidden mr-2 flex-shrink-0">
                  <img src={LOUZINHA_PHOTO} alt="" className="w-full h-full object-cover" />
                </div>
                <div className="bg-white rounded-2xl rounded-bl-md px-4 py-3 shadow-sm border border-[#E6E2DA]">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 bg-[#BFA05F] rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-2 h-2 bg-[#BFA05F] rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-2 h-2 bg-[#BFA05F] rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <form
            onSubmit={sendMessage}
            className="p-4 border-t border-[#E6E2DA] bg-white"
          >
            <div className="flex gap-2">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={t('chat.placeholder')}
                className="flex-1 bg-[#F9F8F6] border border-[#E6E2DA] rounded-full px-4 py-2 text-sm focus:outline-none focus:border-[#BFA05F] transition-colors"
                disabled={isLoading}
                data-testid="louzinha-input"
              />
              <Button
                type="submit"
                disabled={isLoading || !input.trim()}
                className="w-10 h-10 rounded-full bg-[#BFA05F] hover:bg-[#9E8246] p-0"
                data-testid="louzinha-send"
              >
                <Send className="w-4 h-4 text-white" />
              </Button>
            </div>
          </form>
        </div>
      )}
    </>
  );
};

export default Louzinha;